import { FormattedMessage } from 'react-intl';
import { PaloFormGrid } from '@synergy/components-palo';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { ReactNode, useCallback, useRef } from 'react';
import { ScrollToFieldError } from '@synergy/components';
import { errorsToTouch } from '@synergy/utils';

import { FormikGroupItem } from '../../../../../../common/components/new-stepper/formik-group-item';
import { InvoiceSteps } from '../../../models/invoice-steps';
import { PaloInvoiceHeadDelivery } from './palo-head-delivery';
import { PaloInvoiceHeadInvoice, PaloInvoiceHeadOptions } from './palo-head-invoice';
import { PaloInvoiceHeadOrder } from './palo-head-order';
import { PaloInvoiceHeadRecipient } from './palo-head-recipient';
import { PaloInvoiceHeadWatcher } from '../../../palo/palo-invoice-head-watcher';
import { PaloOverviewSectionPaymentTerms } from './palo-section-payment-terms';
import { PaloRemarkPair } from '../../../../../../common/components/palo/palo-remark-pair';
import { PaymentTermAutocompleteRef } from '../../../../../../common/components/payment-term-autocomplete/payment-term-autocomplete';
import {
    invoiceDetailOverviewBaselineSelector,
    invoiceDetailOverviewDataSelector,
} from '../../../store/overview/invoice-detail-overview-selectors';
import { invoiceDetailStepperStateSelector } from '../../../store/invoice-detail-selectors';
import { overviewFieldNames } from '../../../models/overview-form-values';
import { overviewValidationSchema } from '../invoices-detail-overview-validation-schema';
import { submitOverview } from '../../../store/overview/invoice-detail-overview-actions';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';

/**
 * The head as one grid, one row per subject: member, the member's people and
 * order data, supplier, the document's dates and numbers, the order. Labels sit
 * beside their fields and there are no section titles — together that leaves
 * about twelve positions on screen instead of one (#2250).
 */
const INVOICE_HEAD_AREAS = [
    'recipient recipient invoiceAddress invoiceAddress',
    'recipientContact orderContact costCentre functionalAsset',
    'supplier supplier supplierContact supplierContact',
    'invoiceDate deliveryDate incomingInvoiceNumber deliveryNumber',
    'orderReference orderReference orderDate responsibleContact',
];

/** The same head in three columns, for a narrow mask: search fields take two cells or a row of their own. */
const INVOICE_HEAD_NARROW_AREAS = [
    'recipient recipient recipientContact',
    'invoiceAddress invoiceAddress orderContact',
    'supplier supplier supplierContact',
    'invoiceDate deliveryDate incomingInvoiceNumber',
    'deliveryNumber orderDate orderReference',
    'costCentre functionalAsset responsibleContact',
];

export interface PaloInvoiceDetailOverviewProps {
    /** The positions, shown inside the form between delivery and terms. */
    positions?: ReactNode;
}

export const PaloInvoiceDetailOverview = ({ positions }: PaloInvoiceDetailOverviewProps) => {
    const dispatch = useThunkDispatch();
    const overviewData = useAppSelector(invoiceDetailOverviewDataSelector);
    // The invoice as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(invoiceDetailOverviewBaselineSelector);
    const readonly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(InvoiceSteps.Overview);
    const paymentTermAutocompleteRef = useRef<PaymentTermAutocompleteRef>(null);

    const getPaymentTerm = useCallback(
        (id?: number) => paymentTermAutocompleteRef.current?.getPaymentTerm(id) ?? null,
        [paymentTermAutocompleteRef]
    );

    return (
        <FormikGroupItem
            groupItemId={InvoiceSteps.Overview}
            initialValues={overviewData.values}
            onSubmit={data => dispatch(submitOverview(data))}
            validationSchema={overviewValidationSchema()}
            initialErrors={overviewData.errors}
            initialTouched={errorsToTouch(overviewData.errors)}
            enableReinitialize={true}
            validateOnMount={true}
            isDirty={true}
            dirtyBaseline={baseline}
        >
            <PaloThemeProvider>
                <ScrollToFieldError />
                <PaloInvoiceHeadWatcher />
                <PaloInvoiceHeadOptions readonly={readonly} />
                <PaloFormGrid
                    columns={4}
                    labels="side"
                    areas={INVOICE_HEAD_AREAS}
                    narrowAreas={INVOICE_HEAD_NARROW_AREAS}
                >
                    <PaloInvoiceHeadRecipient readonly={readonly} getPaymentTerm={getPaymentTerm} />
                    <PaloInvoiceHeadOrder readonly={readonly} />
                    <PaloInvoiceHeadDelivery readonly={readonly} getPaymentTerm={getPaymentTerm} />
                    <PaloInvoiceHeadInvoice readonly={readonly} />
                </PaloFormGrid>
                {/* What is on the invoice belongs between what is delivered and
                    what it costs, not after the last remark: it is the part one
                    comes for (Besprechung 2026-09-28). */}
                {positions}
                <PaloOverviewSectionPaymentTerms ref={paymentTermAutocompleteRef} readonly={readonly} />
                <PaloRemarkPair
                    external={{
                        name: overviewFieldNames.noteToRecipient,
                        label: <FormattedMessage id="app.header.recipientNote" />,
                    }}
                    internalName={overviewFieldNames.internalNote}
                    readonly={readonly}
                />
            </PaloThemeProvider>
        </FormikGroupItem>
    );
};
